import { useCallback } from 'react';
import { useApiData, useAuthenticatedFetch } from '../client';
import type { Publication, PublicationWithStatus, PublicationSeedResult, ArticlePreviewResponse, ArticleDetail } from '../../types/media-sim';

export function usePublications(options?: { skip?: number; limit?: number }) {
  const params = new URLSearchParams();
  if (options?.skip !== undefined) params.set('skip', String(options.skip));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  const qs = params.toString();
  return useApiData<PublicationWithStatus[]>(
    qs ? `/media-sim/publications?${qs}` : '/media-sim/publications'
  );
}

export function usePublication(publicationId: string) {
  const { data, loading, error, refetch } = useApiData<PublicationWithStatus>(
    publicationId ? `/media-sim/publications/${publicationId}` : ''
  );
  return { publication: data, loading, error, refetch };
}

export function useSyncPublications() {
  const { fetchData, loading } = useAuthenticatedFetch<PublicationSeedResult>();
  const syncPublications = useCallback(
    () =>
      fetchData('/media-sim/publications/seed', {
        method: 'POST',
      }),
    [fetchData]
  );
  return { syncPublications, loading };
}

export function useUpdatePublicationNotes() {
  const { fetchData, loading, error } = useAuthenticatedFetch<PublicationWithStatus>();
  const updateNotes = useCallback(
    (publicationId: string, publicationNotes: string | null) =>
      fetchData(`/media-sim/publications/${publicationId}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ publication_notes: publicationNotes }),
      }),
    [fetchData]
  );
  return { updateNotes, loading, error };
}

export function useDeletePublication() {
  const { fetchData, loading } = useAuthenticatedFetch<void>();
  const deletePublication = useCallback(
    (publicationId: string) =>
      fetchData(`/media-sim/publications/${publicationId}`, {
        method: 'DELETE',
      }),
    [fetchData]
  );
  return { deletePublication, loading };
}

export function usePreviewArticles() {
  const { fetchData, loading, error } = useAuthenticatedFetch<ArticlePreviewResponse>();
  const preview = useCallback(
    (terms: string[], collectionIds: string[], skip = 0, limit = 25) =>
      // List responses omit full_text (include_full_text defaults false); the
      // modal lazy-fetches each article's body via useArticleDetail (#198).
      fetchData('/media-sim/publications/preview-articles', {
        method: 'POST',
        body: JSON.stringify({ terms, collection_ids: collectionIds, skip, limit, include_full_text: false }),
      }),
    [fetchData]
  );
  return { preview, loading, error };
}

// Lazy-fetch a single previewed article's full text by (collection, uuid).
export function useArticleDetail() {
  const { fetchData, loading, error } = useAuthenticatedFetch<ArticleDetail>();
  const fetchArticle = useCallback(
    (collectionId: string, articleUuid: string) =>
      fetchData(
        `/media-sim/publications/${encodeURIComponent(collectionId)}/articles/${encodeURIComponent(articleUuid)}/`
      ),
    [fetchData]
  );
  return { fetchArticle, loading, error };
}

interface SeedResult {
  seeded: number;
  skipped: number;
  total: number;
}

export function useSeedPublications() {
  const { fetchData, loading, error } = useAuthenticatedFetch<SeedResult>();
  const seed = useCallback(
    () => fetchData('/media-sim/publications/seed', { method: 'POST' }),
    [fetchData]
  );
  return { seed, loading, error };
}

// Schedule a background refresh of persisted article count / date-range metadata
// for all publications (admin only). Returns 202 with a scheduled count.
export function useRefreshPublicationMetadata() {
  const { fetchData, loading, error } = useAuthenticatedFetch<{ scheduled: number; message: string }>();
  const refreshMetadata = useCallback(
    () => fetchData('/media-sim/publications/refresh-metadata/', { method: 'POST' }),
    [fetchData]
  );
  return { refreshMetadata, loading, error };
}
